import React, { useEffect, useMemo, useState } from 'react'
import { Alert } from 'antd'
import { RouteComponentProps } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ApplicationState } from 'src/store'
import { getFullRequest } from 'src/core/store/ducks/requests/actions'
import { IconEditRequestTitle } from 'src/components/atoms/edit-request-title'
import { PageHeader } from 'src/components/Ant'
import { RequestUserDataStatus } from 'src/types'
import SegmentControl, { Segments } from 'src/components/SegmentControl'
import * as CSS from './styles'
import RDVButton from '../../components/RDVButton'
import Receipts from '../../components/Receipts'
import RequestHistoryButton from '../../components/request-history-button'
import RequestSummary from '../../components/RequestSummary'

interface Params {
  request_id?: string
}

type SegmentKeys = 'receipts' | 'summary'

type SummarySegments = Segments<SegmentKeys>

function shouldShowRDVButton(status: RequestUserDataStatus) {
  return [
    'APPROVAL_PENDING',
    'UNDERWAY',
    'CONFERENCE_PENDING',
    'CONFERENCE_REPROVED',
    'CLOSING_PENDING',
    'CLOSING_REPROVED',
    'FINISHED'
  ].includes(status)
}

export default function AuditsSummary({
  history,
  match: {
    params: { request_id }
  }
}: RouteComponentProps<Params>) {
  const {
    audits,
    me: {
      user: { user_id, tenant_id, user_level_type }
    },
    requests
  } = useSelector((state: ApplicationState) => ({
    audits: state.audits,
    me: state.me,
    requests: state.requests
  }))

  const dispatch = useDispatch()
  const { t } = useTranslation()

  const isSuperAdmin = ['SUPER_ADMIN'].includes(user_level_type)
  const [current, setCurrent] = useState<SegmentKeys>('summary')

  const segments = useMemo<SummarySegments>(
    () => ({
      summary: {
        disabled: false,
        title: 'web.summary.title',
        renderContent: () => (
          <RequestSummary
            enableEditObservation={false}
            enableSharingEdit={isSuperAdmin ? isSuperAdmin : false}
            request={requests.fullRequest}
          />
        )
      },
      receipts: {
        disabled: false,
        title: 'web.receipts.title',
        renderContent: () => <Receipts audit request={requests.fullRequest} />
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requests.fullRequest]
  )

  useEffect(() => {
    dispatch(getFullRequest(request_id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request_id])

  if (requests.fullRequestLoading || audits.loading) {
    return <CSS.Spin />
  }

  const hasError = requests.fullRequestError || audits.error
  if (hasError) {
    return <Alert message={hasError} showIcon type="error" />
  }

  if (!requests.fullRequest) {
    const message = t('common.response.unexpected-error')
    return <Alert message={message} showIcon type="error" />
  }

  const canAuditorEdit = [334081949181, 597990758089].includes(+tenant_id)
  return (
    <>
      <PageHeader
        extra={[
          <RequestHistoryButton
            key="requestHistoryButton"
            request_id={request_id}
            user_id={user_id}
          />,
          shouldShowRDVButton(requests.fullRequest?.status) && (
            <RDVButton key="btnRDV" request={requests.fullRequest} />
          )
        ]}
        onBack={() => history.goBack()}
        subTitle={
          <>
            {`#${requests.fullRequest.request_number} - ${requests.fullRequest.title} `}
            {canAuditorEdit && (
              <IconEditRequestTitle
                organizationId={requests.fullRequest.organization_id}
                requestId={requests.fullRequest.request_id}
                title={requests.fullRequest.title}
                update={() => dispatch(getFullRequest(request_id))}
              />
            )}
          </>
        }
        title="web.audits.title"
      />
      <CSS.ContainerSegmentControl>
        <SegmentControl
          defaultValue="summary"
          onChange={({ target: { value } }) => {
            setCurrent(value)
          }}
          segments={segments}
          value={current}
        />
      </CSS.ContainerSegmentControl>
      {segments[current].renderContent()}
    </>
  )
}
